import type { ReactElement } from 'react';
import { useWatch } from 'react-hook-form';
import { recompressSourceKind, RecompressPreset, type MediaStorageDto } from '@gr/core/content/media/recompress';
import { useT } from '../../../../app/di';
import { formatFileSize } from '../../../../shared/format';
import { Alert } from '../../../../ui-kit/Alert/Alert';
import { SegmentedControl } from '../../../../ui-kit/Choice/Choice';
import { FieldRow, FieldRowGrow } from '../../../../ui-kit/FieldRow/FieldRow';
import { FormAlert, FormCheckbox, FormSelectField, FormTextField, useServerForm } from '../../../../ui-kit/form/form';
import type { ServerOutcome } from '../../../../ui-kit/form/server-errors';
import {
  AUDIO_OPTIONS,
  FPS_OPTIONS,
  LONG_SIDE_OPTIONS,
  numberFieldError,
  presetFields,
  recompressFormValues,
  selectionSummary,
  SHORT_SIDE_OPTIONS,
  type RecompressFormValues,
  type RecompressNumberField,
} from '../model/recompress-form';
import styles from './RecompressForm.module.scss';

export interface RecompressFormProps {
  id: string;
  storage: MediaStorageDto;
  /** Подтверждение и постановка задачи — у подключённого диалога. */
  onSubmit(values: RecompressFormValues): Promise<ServerOutcome>;
}

const PRESETS = [RecompressPreset.Economy, RecompressPreset.Balanced, RecompressPreset.Quality, RecompressPreset.Custom] as const;

/**
 * MED-UC-9, MED-UC-9.CC-1, MED-UC-9.CC-6: какие форматы пережать, во что и с какими параметрами —
 * пресет или свои; видно, сколько файлов и объёма будет пережато. Отправляется кнопкой с form={id}.
 */
export function RecompressForm({ id, storage, onSubmit }: RecompressFormProps): ReactElement {
  const { t, tp, locale } = useT();
  const { form, submit } = useServerForm<RecompressFormValues>({ defaultValues: recompressFormValues(storage), fields: [], onSubmit });
  const { control, formState, setValue } = form;
  const values = useWatch({ control }) as RecompressFormValues;
  const summary = selectionSummary(storage, values);
  const disabled = formState.isSubmitting;
  const custom = values.preset === RecompressPreset.Custom;
  const { imageTargets, videoTargets } = storage.recompress;
  const sources = storage.formats.filter((format) => recompressSourceKind(format.ext) !== null);

  const choosePreset = (preset: RecompressPreset): void => {
    setValue('preset', preset);
    if (preset === RecompressPreset.Custom) return;
    for (const [field, value] of Object.entries(presetFields(preset))) setValue(field as RecompressNumberField, value, { shouldValidate: true });
  };
  const rules = (field: RecompressNumberField) => ({ validate: (value: unknown) => numberFieldError(field, typeof value === 'string' ? value : '') ?? true });
  const sizeOptions = (options: readonly (number | null)[], empty: string, unit: (n: number) => string) =>
    options.map((option) => ({ value: option === null ? '' : String(option), label: option === null ? empty : unit(option) }));
  const px = (n: number): string => t('media.recompress.px', { n });

  return (
    <form id={id} className="u-stack" onSubmit={submit} noValidate>
      <p className="u-muted u-small">{t('media.recompress.intro')}</p>
      <FormAlert form={form} />
      <fieldset className={styles.group}>
        <legend className={styles.legend}>{t('media.recompress.sources')}</legend>
        <div className={styles.sources}>
          {sources.map((format) => (
            <FormCheckbox
              key={format.ext}
              control={control}
              name={`sources.${format.ext}`}
              disabled={disabled}
              label={t('media.recompress.source', { ext: format.ext.toUpperCase(), files: tp('media.storage.files', format.count, { n: format.count }), size: formatFileSize(format.bytes, locale) })}
            />
          ))}
        </div>
      </fieldset>
      <FieldRow from="sm">
        {summary.images || !summary.videos ? (
          <FieldRowGrow>
            <FormSelectField
              control={control}
              name="imageTarget"
              label={t('media.recompress.imageTarget')}
              disabled={disabled || !summary.images}
              options={imageTargets.map((target) => ({ value: target, label: t(`media.recompress.targets.${target}`) }))}
            />
          </FieldRowGrow>
        ) : null}
        {summary.videos ? (
          <FieldRowGrow>
            <FormSelectField
              control={control}
              name="videoTarget"
              label={t('media.recompress.videoTarget')}
              disabled={disabled}
              options={videoTargets.map((target) => ({ value: target, label: t(`media.recompress.targets.${target}`) }))}
            />
          </FieldRowGrow>
        ) : null}
      </FieldRow>
      <div className="u-stack u-gap-2">
        <SegmentedControl
          label={t('media.recompress.preset')}
          options={PRESETS.map((preset) => ({ value: preset, label: t(`media.recompress.presets.${preset}`) }))}
          value={values.preset}
          onChange={choosePreset}
        />
        <p className="u-muted u-small">{t(`media.recompress.presetHints.${values.preset}`)}</p>
      </div>
      <fieldset className={styles.group} disabled={disabled || !custom} aria-label={t('media.recompress.preset')}>
        {summary.images || !summary.videos ? (
          <FieldRow from="sm">
            <FieldRowGrow>
              <FormTextField control={control} name="quality" label={t('media.recompress.quality')} hint={t('media.recompress.qualityHint')} inputMode="numeric" rules={rules('quality')} />
            </FieldRowGrow>
            <FieldRowGrow>
              <FormSelectField control={control} name="maxLongSide" label={t('media.recompress.maxLongSide')} options={sizeOptions(LONG_SIDE_OPTIONS, t('media.recompress.noResize'), px)} />
            </FieldRowGrow>
          </FieldRow>
        ) : null}
        {summary.videos ? (
          <>
            <FieldRow from="sm">
              <FieldRowGrow>
                <FormTextField control={control} name="crf" label={t('media.recompress.crf')} hint={t('media.recompress.crfHint')} inputMode="numeric" rules={rules('crf')} />
              </FieldRowGrow>
              <FieldRowGrow>
                <FormSelectField control={control} name="maxShortSide" label={t('media.recompress.maxShortSide')} options={sizeOptions(SHORT_SIDE_OPTIONS, t('media.recompress.noResize'), px)} />
              </FieldRowGrow>
            </FieldRow>
            <FieldRow from="sm">
              <FieldRowGrow>
                <FormSelectField
                  control={control}
                  name="maxFps"
                  label={t('media.recompress.maxFps')}
                  options={sizeOptions(FPS_OPTIONS, t('media.recompress.sameFps'), (n) => t('media.recompress.fps', { n }))}
                />
              </FieldRowGrow>
              <FieldRowGrow>
                <FormSelectField
                  control={control}
                  name="audioKbps"
                  label={t('media.recompress.audioKbps')}
                  options={AUDIO_OPTIONS.map((n) => ({ value: String(n), label: t('media.recompress.kbps', { n }) }))}
                />
              </FieldRowGrow>
            </FieldRow>
          </>
        ) : null}
        <FormTextField
          control={control}
          name="minSavingsPercent"
          label={t('media.recompress.minSavings')}
          hint={t('media.recompress.minSavingsHint')}
          inputMode="numeric"
          rules={rules('minSavingsPercent')}
        />
      </fieldset>
      {summary.files === 0 ? (
        <Alert tone="info">{t('media.recompress.noSources')}</Alert>
      ) : (
        <p className={styles.summary}>{t('media.recompress.summary', { files: tp('media.storage.files', summary.files, { n: summary.files }), size: formatFileSize(summary.bytes, locale) })}</p>
      )}
    </form>
  );
}
